import { type AssetDropDestination, type VideoRejectionReason } from './canvasAssetDropTypes';
import { VIDEO_TO_GIF_MAX_DURATION_SECONDS, VIDEO_TO_GIF_MAX_FILE_SIZE_MB } from './videoToGifLimits';

export const getVideoLimitDetail = (rejections: VideoRejectionReason[]): string => {
  if (rejections.length === 0) {
    return '';
  }

  const hasSize = rejections.some((r) => r === 'size' || r === 'both');
  const hasDuration = rejections.some((r) => r === 'duration' || r === 'both');
  const hasUnknown = rejections.some((r) => r === 'unknown');

  if (hasUnknown && !hasSize && !hasDuration) {
    return 'exceeds size or duration limit';
  }

  const parts: string[] = [];

  if (hasSize) {
    parts.push(`exceeds ${VIDEO_TO_GIF_MAX_FILE_SIZE_MB}MB`);
  }
  if (hasDuration) {
    parts.push(`exceeds ${VIDEO_TO_GIF_MAX_DURATION_SECONDS}s`);
  }

  return parts.join(' and ');
};

/**
 * Builds the toast message for asset drop, matching selection bar copy.
 * Single message combines success, unsupported, and/or video-limit-exceeded counts.
 */
export const getAssetDropToastMessage = (
  addedCount: number,
  unsupportedCount: number,
  destination: AssetDropDestination,
  videoRejections: VideoRejectionReason[] = [],
): string => {
  if (addedCount <= 0 && unsupportedCount <= 0 && videoRejections.length <= 0) {
    return '';
  }

  const destinationLabel = destination === 'canvas' ? 'Canvas' : 'chat';
  const addedText =
    addedCount > 0 ? `${addedCount} ${addedCount === 1 ? 'item' : 'items'} added to ${destinationLabel}` : '';
  const unsupportedItemLabel = unsupportedCount === 1 ? 'item' : 'items';
  const unsupportedTypeLabel = unsupportedCount === 1 ? 'unsupported type' : 'unsupported types';
  const unsupportedText =
    unsupportedCount > 0
      ? `${unsupportedCount} ${unsupportedItemLabel} couldn't be added (${unsupportedTypeLabel})`
      : '';
  const videoLimitText =
    videoRejections.length > 0
      ? `${videoRejections.length} ${videoRejections.length === 1 ? 'video' : 'videos'} couldn't be added (${getVideoLimitDetail(videoRejections)})`
      : '';

  return [addedText, unsupportedText, videoLimitText].filter(Boolean).join('. ');
};
